import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DatePicker from "react-datepicker";
import { ro } from "date-fns/locale/ro";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import './CreateRevisionPO.css';
import axiosInstance from '../../../Config/axiosInstance';
import BrowseProduct from './BrowseProduct';
import { NumericFormat } from "react-number-format";
import Select from 'react-select';

//inregistram localizarea pentru react-datepicker
registerLocale("ro", ro);

const CreatePORevision = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [suppliers, setSuppliers] = useState([]);
  const [delivery_address, setDeliveryAddress] = useState([]);
  const [projectCode, setProjectCode] = useState([]);
  const [subprojectCode, setSubprojectCode] = useState([]);
  const [currency, setCurrency] = useState([]);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [systemCurrencyId, setSystemCurrencyId] = useState(null);
  const [product, setProduct] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedProductsList, setSelectedProductsList] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [productToDeleteIndex, setProductToDeleteIndex] = useState(null);
  const [procurementId, setProcurementId] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderSummary, setOrderSummary] = useState(null);
  const calculationInProgress = useRef(false);
  const [arrivalDeadline, setArrivalDeadline] = useState(null);
  const [isSubprojectCodeDisabled, setIsSubprojectCodeDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  const [formData, setFormData] = useState({
    order_number: "",
    project_code: "",
    subproject_code: "",
    supplier: "",
    arrival_deadline: "",
    payment_term: "",
    exchange_rate: "",
    currency: "",
    tax: "",
    note_to_supplier: "",
    status: "",
    total_wo_vat: "",
    vat_value: "",
    total: "",
    delivery_address: "",
    delivery_conditions: "",
    vat_value_tax: "",
    tax_value: "",
    note_revision: "",
  });

  //helper: converteste orice (inclusiv array [0]) intr-un numar finit; altfel 0
  const toNumber = useCallback((v) => {
    if (Array.isArray(v)) v = v[0];
    if (v === null || v === undefined || v === '') return 0;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  }, []);

  //helper: despacheteaza array [x] -> x (pentru id-uri)
  const unwrapId = useCallback((v) => {
    return Array.isArray(v) ? v[0] : v;
  }, []);

  //functie pentru formatarea datei
  const formatDate = useCallback((dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth()+1).toString().padStart(2, '0')}.${date.getFullYear()}`;
  }, []);

  const exchangeOptionsByCurrencyId = useMemo(() => {
    const m = new Map();
    
    for (const rate of exchangeRates) {
      const currencyId = rate.currency;
      if (!m.has(currencyId)) m.set(currencyId, []);
      
      const formattedValue = new Intl.NumberFormat('ro-RO', {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4
      }).format(Number(rate.value));
      
      m.get(currencyId).push({
        value: rate.id,
        label: `${formattedValue} (${formatDate(rate.date)})`,
        shortLabel: formattedValue,
        rateValue: Number(rate.value),
        rateDate: rate.date,
        currency: currencyId
      });
    }
    
    for (const [currencyId, arr] of m.entries()) {
      arr.sort((a, b) => new Date(b.rateDate) - new Date(a.rateDate));
      m.set(currencyId, arr);
    }
    
    return m;
  }, [exchangeRates, formatDate]);

  const getCurrentExchangeRate = useCallback(() => {
    if (!formData.exchange_rate) return null;
    const selectedRate = exchangeRates.find(rate => rate.id === parseInt(formData.exchange_rate));
    return selectedRate ? selectedRate.value : null;
  }, [formData.exchange_rate, exchangeRates]);

  const roundToFourDecimals = useCallback((value) => {
    if (!value && value !== 0) return 0;
    const num = parseFloat(value);
    if (isNaN(num)) return 0;
    return Math.round(num * 10000) / 10000;
  }, []);

  const calculateTotals = useCallback(async (items, tax_value, vat_value_tax, currencyId, exchangeRateValue) => {
    try {
      setIsCalculating(true);
      const response = await axiosInstance.post('/purchase-order/calculate-po/', {
        items: items,
        tax_value: roundToFourDecimals(tax_value),
        vat_value_tax: roundToFourDecimals(vat_value_tax),
        currency_id: currencyId ? parseInt(currencyId) : null,
        exchange_rate_value: exchangeRateValue
      });
      
      if (response.data) {
        if (response.data.summary) {
          response.data.summary.total_without_vat = roundToFourDecimals(response.data.summary.total_without_vat);
          response.data.summary.total_vat = roundToFourDecimals(response.data.summary.total_vat);
          response.data.summary.delivery_tax = roundToFourDecimals(response.data.summary.delivery_tax);
          response.data.summary.delivery_vat = roundToFourDecimals(response.data.summary.delivery_vat);
          response.data.summary.total_delivery = roundToFourDecimals(response.data.summary.total_delivery);
          response.data.summary.total = roundToFourDecimals(response.data.summary.total);
          
          response.data.summary.total_without_vat_system = roundToFourDecimals(response.data.summary.total_without_vat_system || 0);
          response.data.summary.total_vat_system = roundToFourDecimals(response.data.summary.total_vat_system || 0);
          response.data.summary.delivery_tax_system = roundToFourDecimals(response.data.summary.delivery_tax_system || 0);
          response.data.summary.total_delivery_system_currency = roundToFourDecimals(response.data.summary.total_delivery_system_currency || 0);
          response.data.summary.total_system_currency = roundToFourDecimals(response.data.summary.total_system_currency || 0);
        }
        if (response.data.items) {
          response.data.items = response.data.items.map(item => ({
            ...item,
            subtotal: roundToFourDecimals(item.subtotal),
            vat_amount: roundToFourDecimals(item.vat_amount),
            total: roundToFourDecimals(item.total),
            total_system_currency: roundToFourDecimals(item.total_system_currency || 0)
          }));
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('Error calculating totals:', error);
      return null;
    } finally {
      setIsCalculating(false);
    }
  }, [roundToFourDecimals]);

  const calculateItemTotals = useCallback(async (quantity, unit_price, vat_percent, currencyId, exchangeRateValue) => {
    try {
      const response = await axiosInstance.post('/purchase-order/calculate-item/', {
        quantity: roundToFourDecimals(quantity),
        unit_price: roundToFourDecimals(unit_price),
        vat_value: roundToFourDecimals(vat_percent),
        currency_id: currencyId ? parseInt(currencyId) : null,
        exchange_rate_value: exchangeRateValue
      });
      
      if (response.data) {
        response.data.subtotal = roundToFourDecimals(response.data.subtotal);
        response.data.vat_amount = roundToFourDecimals(response.data.vat_amount);
        response.data.total = roundToFourDecimals(response.data.total);
        response.data.total_system_currency = roundToFourDecimals(response.data.total_system_currency || 0);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error calculating item:', error);
      return null;
    }
  }, [roundToFourDecimals]);

  const recalculateOrderTotals = useCallback(async () => {
    if (selectedProductsList.length === 0) {
      setOrderSummary(null);
      setFormData(prev => ({
        ...prev,
        total_wo_vat: 0,
        vat_value: 0,
        total: 0
      }));
      return;
    }

    const itemsForCalculation = selectedProductsList.map(prod => ({
      product: unwrapId(prod.id),
      quantity: roundToFourDecimals(toNumber(prod.quantity)),
      unit_price: roundToFourDecimals(toNumber(prod.cost)),
      vat_value: roundToFourDecimals(toNumber(prod.vat))
    }));
    
    const exchangeRateValue = getCurrentExchangeRate();
    const currencyId = formData.currency;
    
    try {
      const result = await calculateTotals(
        itemsForCalculation,
        roundToFourDecimals(toNumber(formData.tax_value)),
        roundToFourDecimals(toNumber(formData.vat_value_tax)),
        currencyId,
        exchangeRateValue
      );
      
      if (result) {
        setFormData(prev => ({
          ...prev,
          total_wo_vat: roundToFourDecimals(result.calculated_values?.total_wo_vat || result.summary.total_without_vat),
          vat_value: roundToFourDecimals(result.calculated_values?.vat_value || result.summary.total_vat),
          total: roundToFourDecimals(result.calculated_values?.total || result.summary.total)
        }));
        
        if (result.summary) {
          const roundedSummary = {
            total_without_vat: roundToFourDecimals(result.summary.total_without_vat),
            total_vat: roundToFourDecimals(result.summary.total_vat),
            delivery_tax: roundToFourDecimals(result.summary.delivery_tax),
            delivery_vat: roundToFourDecimals(result.summary.delivery_vat),
            total_delivery: roundToFourDecimals(result.summary.total_delivery),
            total: roundToFourDecimals(result.summary.total),
            total_without_vat_system: roundToFourDecimals(result.summary.total_without_vat_system || 0),
            total_vat_system: roundToFourDecimals(result.summary.total_vat_system || 0),
            delivery_tax_system: roundToFourDecimals(result.summary.delivery_tax_system || 0),
            total_delivery_system_currency: roundToFourDecimals(result.summary.total_delivery_system_currency || 0),
            total_system_currency: roundToFourDecimals(result.summary.total_system_currency || 0)
          };
          setOrderSummary(roundedSummary);
        }
      }
    } catch (error) {
      console.error('Error recalculating order totals:', error);
    }
  }, [selectedProductsList, formData.tax_value, formData.vat_value_tax, formData.currency, calculateTotals, roundToFourDecimals, getCurrentExchangeRate, toNumber, unwrapId]);

  const handleDateChange = useCallback((date) => {
    if (!date) {
      setFormData(prev => ({
        ...prev,
        arrival_deadline: ""
      }));
      setArrivalDeadline(null);
      return;
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    const formattedDate = `${year}-${month}-${day}`;

    const oldHeaderDate = formData.arrival_deadline;

    setFormData(prev => ({
      ...prev,
      arrival_deadline: formattedDate
    }));

    setArrivalDeadline(date);

    setSelectedProductsList(prev =>
      prev.map(prod => ({
        ...prod,
        arrival_deadline:
          prod.arrival_deadline === oldHeaderDate
            ? formattedDate
            : prod.arrival_deadline
      }))
    );
  }, [formData.arrival_deadline]);

  const supplierOptions = suppliers.map(supplier => ({
    value: supplier.id,
    label: supplier.name,
    address: supplier.address
  }));

  const deliveryAddressOptions = delivery_address.map(address => ({
    value: address.id,
    label: address.name,
    address: address.address
  }));

  const projectCodeOptions = projectCode.map(code => ({
    value: code.id,
    label: code.code,
  }));

  const subprojectCodeOptions = subprojectCode.map(code => ({
    value: code.id,
    label: code.code,
  }));

  //functie pentru incarcarea subproject code in functie de project code
  const fetchSubprojectCodesByProject = useCallback(async(projectCodeId) => {
    if(!projectCodeId) {
      setSubprojectCode([]);
      return;
    }

    try {
      const response = await axiosInstance.get(
        `/subprojectcodes/by_project/${projectCodeId}/`
      );
      setSubprojectCode(response.data);
    } catch (error) {
      console.error("Error fetching subproject codes: ", error);
      setSubprojectCode([]);
    }
  }, []);

  const selectedSupplierAddress =
    suppliers.find(s => String(s.id) === String(formData.supplier))?.address || "";

  const selectedDeliveryAddress =
    delivery_address.find(d => String(d.id) === String(formData.delivery_address))?.address || "";

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.supplier) newErrors.supplier = true;
    if (!formData.delivery_address) newErrors.delivery_address = true;
    if (!formData.project_code) newErrors.project_code = true;
    if (!formData.arrival_deadline) newErrors.arrival_deadline = true;
    if (!formData.payment_term) newErrors.payment_term = true;
    if (!formData.currency) newErrors.currency = true;
    if (!formData.delivery_conditions) newErrors.delivery_conditions = true;
    
    if(!formData.note_revision || formData.note_revision.trim() === "") {
      newErrors.note_revision = true;
    }
    
    const isSystemCurrency = systemCurrencyId === parseInt(formData.currency);
    if(!formData.exchange_rate && !isSystemCurrency && formData.currency) {
      newErrors.exchange_rate = true;
    }

    if (formData.tax) {
      if (!formData.vat_value_tax || parseFloat(formData.vat_value_tax) <= 0) {
        newErrors.vat_value_tax = true;
      }
      if (!formData.tax_value || parseFloat(formData.tax_value) <= 0) {
        newErrors.tax_value = true;
      }
    }

    if (selectedProductsList.length === 0) {
      newErrors.product = true;
    }

    selectedProductsList.forEach((prod, index) => {
      if (prod.quantity === '' || prod.quantity == null || toNumber(prod.quantity) <= 0) {
        newErrors[`quantity_${index}`] = true;
      }
      if (!prod.um || (typeof prod.um === 'string' && prod.um.trim() === "")) {
        newErrors[`um_${index}`] = true;
      }
      if (prod.vat === '' || prod.vat === null || prod.vat === undefined) {
        newErrors[`vat_${index}`] = true;
      }
      if (prod.cost === '' || prod.cost === null || prod.cost === undefined) {
        newErrors[`cost_${index}`] = true;
      }

      if (!prod.arrival_deadline) newErrors[`arrival_${index}`] = true;
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, selectedProductsList, systemCurrencyId, toNumber]);

  const handleSelectChange = useCallback((selectOption, { name }) => {
    setFormData(prev => ({
      ...prev,
      [name]: selectOption ? selectOption.value : ""
    }));
    
    //cand se selecteaza/deselecteaza un project code
    if(name === "project_code") {
      const projectCodeId = selectOption ? selectOption.value : null;

      if(projectCodeId) {
        //incarca subproject code si activeaza selectul
        fetchSubprojectCodesByProject(projectCodeId);
        setIsSubprojectCodeDisabled(false);
        //reseteaza subproject_code
        setFormData(prev => ({
          ...prev,
          subproject_code: ""
        }));
      } else {
        //goleste si dezactiveaza subproject code
        setSubprojectCode([]);
        setIsSubprojectCodeDisabled(true);
        setFormData(prev => ({
          ...prev,
          subproject_code: ""
        }));
      }
    }
  }, [fetchSubprojectCodesByProject]);

  useEffect(() => {
    const fetchPurchaseOrder = async () => {
      try {
        setIsLoading(true);
        const response = await axiosInstance.get(`/purchase-order/${id}/`);
        const po = response.data;

        setFormData({
          supplier: po.supplier || "",
          order_number: po.order_number || "",
          project_code: po.project_code || "",
          subproject_code: po.subproject_code || "",
          arrival_deadline: po.arrival_deadline || "",
          payment_term: po.payment_term || "",
          exchange_rate: po.exchange_rate || "",
          currency: po.currency || "",
          tax: po.tax || "",
          note_to_supplier: po.note_to_supplier || "",
          status: po.status || "",
          total_wo_vat: po.total_wo_vat || "",
          total: po.total || "",
          vat_value: po.vat_value || "",
          delivery_address: po.delivery_address || "",
          delivery_conditions: po.delivery_conditions || "",
          vat_value_tax: roundToFourDecimals(po.items?.[0]?.vat_value_tax || 0),
          tax_value: roundToFourDecimals(po.tax_value || 0)
        });

        if (po.arrival_deadline) {
          setArrivalDeadline(new Date(po.arrival_deadline));
        }

        if (po.items && po.items.length > 0) {
          const loadedItems = po.items.map(item => ({
            id: item.product_detail.id,
            title: item.product_detail.title,
            productId: item.product_detail.id,
            quantity: roundToFourDecimals(parseFloat(item.quantity) || 0),
            vat: roundToFourDecimals(parseFloat(item.vat_value) || 0),
            um: item.unit,
            tax: item.tax?.value || "",
            tax_id: item.tax?.id || "",
            cost: roundToFourDecimals(parseFloat(item.unit_price) || 0),
            vat_value_tax: roundToFourDecimals(parseFloat(item.vat_value_tax) || 0),
            purchase_order_item_id: item.id,
            total: roundToFourDecimals(parseFloat(item.total) || 0),
            total_system_currency: roundToFourDecimals(parseFloat(item.total_system_currency) || 0),
            arrival_deadline: item.arrival_deadline || "",
            subtotal: roundToFourDecimals(parseFloat(item.total_wo_vat) || 0),
            vat_amount: roundToFourDecimals(parseFloat(item.vat_value_amount) || 0)
          }));
          setSelectedProductsList(loadedItems);
        }

      } catch (error) {
        console.error("Error fetching purchase order: ", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchPurchaseOrder();
    } else {
      setIsLoading(false);
    }
  }, [id, roundToFourDecimals]);

  useEffect(() => {
    if (selectedProductsList.length > 0 && !calculationInProgress.current) {
      recalculateOrderTotals();
    }
  }, [selectedProductsList, formData.tax_value, formData.vat_value_tax, recalculateOrderTotals]);

  useEffect(() => {
    if (selectedProductsList.length > 0 && !calculationInProgress.current) {
      recalculateOrderTotals();
    }
  }, [formData.currency, formData.exchange_rate, recalculateOrderTotals, selectedProductsList.length]);

  useEffect(() => {
    const recalculateAllItems = async () => {
      if (selectedProductsList.length === 0) return;

      const exchangeRateValue = getCurrentExchangeRate();
      const currencyId = formData.currency;

      if (calculationInProgress.current) return;
      calculationInProgress.current = true;

      try {
        const updatedList = [...selectedProductsList];
        let hasChanges = false;

        for (let i = 0; i < updatedList.length; i++) {
          const item = updatedList[i];
          const quantity = roundToFourDecimals(toNumber(item.quantity));
          const cost = roundToFourDecimals(toNumber(item.cost));
          const vat = roundToFourDecimals(toNumber(item.vat));

          if (quantity > 0) {
            const result = await calculateItemTotals(quantity, cost, vat, currencyId, exchangeRateValue);
            if (result) {
              const newSubtotal = roundToFourDecimals(result.subtotal);
              const newVatAmount = roundToFourDecimals(result.vat_amount);
              const newTotal = roundToFourDecimals(result.total);
              const newTotalSystem = roundToFourDecimals(result.total_system_currency || 0);
              
              if (item.subtotal !== newSubtotal || 
                  item.vat_amount !== newVatAmount || 
                  item.total !== newTotal ||
                  item.total_system_currency !== newTotalSystem) {
                updatedList[i].subtotal = newSubtotal;
                updatedList[i].vat_amount = newVatAmount;
                updatedList[i].total = newTotal;
                updatedList[i].total_system_currency = newTotalSystem;
                hasChanges = true;
              }
            }
          }
        }
        if (hasChanges) {
          setSelectedProductsList(updatedList);
          await recalculateOrderTotals();
        }
      } catch (error) {
        console.error('Error recalculating all items: ', error);
      } finally {
        calculationInProgress.current = false;
      }
    };

    if (selectedProductsList.length > 0 && formData.currency) {
      recalculateAllItems();
    }
  }, [formData.currency, formData.exchange_rate, systemCurrencyId, selectedProductsList, getCurrentExchangeRate, roundToFourDecimals, calculateItemTotals, recalculateOrderTotals, toNumber]);

  const getStatusClass = useCallback((status) => {
    switch (status) {
      case 'RECEIVED':
        return 'draft-status-badge draft-status-received';
      case 'DRAFT':
        return 'draft-status-badge draft-status-draft';
      case 'ORDERED':
        return 'draft-status-badge draft-status-ordered';
      case 'CANCELED':
        return 'draft-status-badge draft-status-canceled';
      case 'WAITING APPROVAL':
        return 'draft-status-badge draft-status-wappr';
      case 'APPROVED':
        return 'draft-status-badge draft-status-approved';
      case 'REJECTED':
        return 'draft-status-badge draft-status-rejected';
      case 'PARTIALLY RECEIVED':
        return 'draft-status-badge draft-status-partially';
      default:
        return 'draft-status-badge';
    }
  }, []);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axiosInstance.get('/departments/');
        const procurementDept = response.data.find(dept => dept.name === "Procurement");
        if (procurementDept) {
          setProcurementId(procurementDept.id);
        }
      } catch (error) {
        console.error("Error fetching departments: ", error);
      }
    };

    fetchDepartments();
  }, []);

  useEffect(() => {
    if (!procurementId) return;

    const fetchActiveSuppliers = async () => {
      try {
        const response = await axiosInstance.get(
          `/suppliers/active-by-department/?department=${procurementId}`
        );
        setSuppliers(response.data);
      } catch (error) {
        console.error("Error fetching active suppliers: ", error);
      }
    };

    fetchActiveSuppliers();
  }, [procurementId]);

  useEffect(() => {
    const fetchAddressDelivery = async () => {
      try {
        const response = await axiosInstance.get('/delivery-address/deliveryAddress/');
        setDeliveryAddress(response.data);
      } catch (error) {
        console.error('Error fetching address delivery: ', error);
      }
    };
    fetchAddressDelivery();
  }, []);

  useEffect(() => {
    const fetchProjectCode = async () => {
      try {
        const response = await axiosInstance.get('/code_registries/');
        setProjectCode(response.data);
      } catch (error) {
        console.error('Error fetching project code: ', error);
      }
    };
    fetchProjectCode();
  }, []);

  useEffect(() => {
    const fetchSubprojectCode = async() => {
      try {
        const response = await axiosInstance.get('/subprojectcodes/');
        setSubprojectCode(response.data);
      } catch (error) {
        console.error("Error fetching subproject code: ", error);
      }
    }
    fetchSubprojectCode();
  }, []);

  useEffect(() => {
    const fetchCurrency = async () => {
      try {
        const response = await axiosInstance.get('/currencies/');
        setCurrency(response.data);
      } catch (error) {
        console.error('Error fetching currency: ', error);
      }
    };
    fetchCurrency();
  }, []);

  useEffect(() => {
    const fetchExchangeRates = async () => {
      try {
        const response = await axiosInstance.get('/exchange-rate/');
        const sortedRates = (response.data || []).sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );
        setExchangeRates(sortedRates);
      } catch (error) {
        console.error('Error fetching exchange rate: ', error);
      }
    };
    fetchExchangeRates();
  }, []);

  useEffect(() => {
    const fetchSystemCurrency = async () => {
      try {
        const response = await axiosInstance.get('/system-currency/1/');
        setSystemCurrencyId(response.data?.currency ?? null);
      } catch (error) {
        console.error('Error fetching system currency: ', error);
      }
    };
    fetchSystemCurrency();
  }, []);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axiosInstance.get('/product/');
        const activeProducts = response.data.filter(product => product.status === "ACTIVE");
        setProduct(activeProducts);
        setFilteredProducts(activeProducts);
      } catch (error) {
        console.error('Error fetching product: ', error);
      }
    };
    fetchProduct();
  }, []);

  useEffect(() => {
    const fetchTaxes = async () => {
      try {
        const response = await axiosInstance.get('/taxes/taxes/');
        setTaxes(response.data);
      } catch (error) {
        console.error('Error fetching taxes: ', error);
      }
    };
    fetchTaxes();
  }, []);

  //daca exista deja un project code selectat, incarca subproject code urile asociate codului de proiect
  useEffect(() => {
    if(formData.project_code) {
      fetchSubprojectCodesByProject(formData.project_code);
      setIsSubprojectCodeDisabled(false);
    }
  }, [formData.project_code, fetchSubprojectCodesByProject]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    if (name === "tax_value") {
      setFormData(prev => ({ ...prev, [name]: roundToFourDecimals(toNumber(value)) }));
    } else if (name === "vat_value_tax") {
      setFormData(prev => ({ ...prev, [name]: roundToFourDecimals(toNumber(value)) }));
    } else if (name === "tax") {
      const selectedTax = taxes.find(tax => String(tax.id) === String(value));

      if (!value) {
        setFormData(prev => ({
          ...prev,
          tax: "",
          vat_value_tax: 0,
          tax_value: 0,
        }));
      } else if (selectedTax) {
        setFormData(prev => ({
          ...prev,
          tax: value,
          vat_value_tax: roundToFourDecimals(selectedTax.value ?? 21),
          tax_value: 0,
        }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  }, [taxes, roundToFourDecimals, toNumber]);

  const handleProductSelect = useCallback((prod) => {
    setSelectedProductsList(prev => [...prev, {
      id: prod.id,
      productId: prod.id,
      title: prod.title || prod.name,
      quantity: '',
      um: '',
      vat: '21',
      cost: '',
      tax_id: '',
      vat_value_tax: '',
      total: 0,
      total_system_currency: 0,
      subtotal: 0,
      vat_amount: 0,
      arrival_deadline: formData.arrival_deadline || ""
    }]);
    setSearchTerm('');
    setFilteredProducts([]);
  }, [formData.arrival_deadline]);

  const handleProductLineChange = useCallback(async (index, field, value) => {
    const updatedList = [...selectedProductsList];
    
    if (field === "quantity" || field === "cost" || field === "vat") {
      updatedList[index][field] = roundToFourDecimals(toNumber(value));
    } else {
      updatedList[index][field] = value;
    }
    
    if (field === "quantity" || field === "cost" || field === "vat") {
      const quantity = roundToFourDecimals(toNumber(updatedList[index].quantity));
      const cost = roundToFourDecimals(toNumber(updatedList[index].cost));
      const vat = roundToFourDecimals(toNumber(updatedList[index].vat));
      
      if (quantity > 0) {
        try {
          const exchangeRateValue = getCurrentExchangeRate();
          const currencyId = formData.currency;
          
          const result = await calculateItemTotals(quantity, cost, vat, currencyId, exchangeRateValue);
          if (result) {
            updatedList[index].subtotal = roundToFourDecimals(result.subtotal);
            updatedList[index].vat_amount = roundToFourDecimals(result.vat_amount);
            updatedList[index].total = roundToFourDecimals(result.total);
            updatedList[index].total_system_currency = roundToFourDecimals(result.total_system_currency || 0);
          }
        } catch (error) {
          console.error('Error calculating item:', error);
        }
      } else {
        updatedList[index].subtotal = 0;
        updatedList[index].vat_amount = 0;
        updatedList[index].total = 0;
        updatedList[index].total_system_currency = 0;
      }
      
      setSelectedProductsList(updatedList);
    } else {
      setSelectedProductsList(updatedList);
    }
  }, [selectedProductsList, calculateItemTotals, roundToFourDecimals, getCurrentExchangeRate, formData.currency, toNumber]);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredProducts([]);
    } else {
      const filtered = product.filter(p =>
        p.title?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !selectedProductsList.some(selected => selected.id === p.id)
      );
      setFilteredProducts(filtered);
    }
  }, [searchTerm, product, selectedProductsList]);

  const handleDeleteProduct = useCallback((index) => {
    const updatedList = [...selectedProductsList];
    updatedList.splice(index, 1);
    setSelectedProductsList(updatedList);
  }, [selectedProductsList]);

  const handleAskDeleteProduct = useCallback((index) => {
    setProductToDeleteIndex(index);
    setShowConfirmModal(true);
  }, []);

  const confirmDeleteProduct = useCallback(() => {
    if (productToDeleteIndex !== null) {
      handleDeleteProduct(productToDeleteIndex);
      setProductToDeleteIndex(null);
      setShowConfirmModal(false);
    }
  }, [productToDeleteIndex, handleDeleteProduct]);

  const getSelectedCurrencyLabel = useCallback(() => {
    const selected = currency.find(c => String(c.id) === String(formData.currency));
    return selected ? selected.name : '';
  }, [currency, formData.currency]);

  const getSystemCurrencyLabel = useCallback(() => {
    const systemCurrency = currency.find(c => c.id === systemCurrencyId);
    return systemCurrency ? systemCurrency.name : '';
  }, [currency, systemCurrencyId]);

  const handleSaveChanges = useCallback(async () => {
    //previne click uri multiple
    if (isSubmitting) {
      console.log("Submission already in progress, ignoring click");
      return;
    }

    //validare form
    if (!validateForm()) {
      //scroll la primul camp cu eroare
      const firstError = document.querySelector('.input-error');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsSubmitting(true);

    const exchangeRateValue = getCurrentExchangeRate();

    const updatedItems = selectedProductsList.map(item => {
      const quantity = roundToFourDecimals(toNumber(item.quantity));
      const cost = roundToFourDecimals(toNumber(item.cost));
      const vat = roundToFourDecimals(toNumber(item.vat));

      return {
        id: unwrapId(item.purchase_order_item_id) || null,
        purchase_order: parseInt(id),
        product: unwrapId(item.productId) || unwrapId(item.id),
        unit: item.um,
        quantity,
        unit_price: cost,
        tax: unwrapId(item.tax_id) ? parseInt(unwrapId(item.tax_id)) : (unwrapId(formData.tax) ? parseInt(unwrapId(formData.tax)) : null),
        total_wo_vat: roundToFourDecimals(toNumber(item.subtotal)),
        total: roundToFourDecimals(toNumber(item.total)),
        total_system_currency: roundToFourDecimals(toNumber(item.total_system_currency)),
        vat_value: vat,
        vat_value_tax: roundToFourDecimals(toNumber(formData.vat_value_tax)),
        tax_value: roundToFourDecimals(toNumber(formData.tax_value)),
        arrival_deadline: item.arrival_deadline
      };
    });

    const payload = {
      supplier: unwrapId(formData.supplier) ? parseInt(unwrapId(formData.supplier)) : null,
      delivery_address: unwrapId(formData.delivery_address) ? parseInt(unwrapId(formData.delivery_address)) : null,
      project_code: unwrapId(formData.project_code) ? parseInt(unwrapId(formData.project_code)) : null,
      subproject_code: formData.subproject_code ? parseInt(unwrapId(formData.subproject_code)) : null,
      arrival_deadline: formData.arrival_deadline,
      payment_term: formData.payment_term ? parseInt(formData.payment_term, 10) : null,
      currency: unwrapId(formData.currency) ? parseInt(unwrapId(formData.currency)) : null,
      exchange_rate: formData.exchange_rate ? parseInt(unwrapId(formData.exchange_rate)) : null,
      exchange_rate_value: exchangeRateValue,
      delivery_conditions: formData.delivery_conditions,
      note_to_supplier: formData.note_to_supplier || "",
      tax: formData.tax ? parseInt(unwrapId(formData.tax)) : null,
      vat_value_tax: roundToFourDecimals(toNumber(formData.vat_value_tax)),
      tax_value: roundToFourDecimals(toNumber(formData.tax_value)),
      total_wo_vat: roundToFourDecimals(toNumber(formData.total_wo_vat)),
      vat_value: roundToFourDecimals(toNumber(formData.vat_value)),
      total: roundToFourDecimals(toNumber(formData.total)),
      note_revision: formData.note_revision,
      items: updatedItems,
    };
    
    try {
      //creeaza revizia
      const revisionResponse = await axiosInstance.post(
        `/purchase-order/${id}/create-revision/`,
        payload
      );

      const newOrderId = revisionResponse?.data?.id;

      if (!newOrderId) {
        throw new Error("No new order ID returned from create-revision");
      }

      //verifica daca inregistrarea exista in baza de date
      let recordExists = false;
      let attempts = 0;
      const maxAttempts = 10;
      const delayMs = 300;

      console.log(`Checking if order ${newOrderId} exists in database...`);

      while (!recordExists && attempts < maxAttempts) {
        try {
          //obtine inregistrarea
          const checkResponse = await axiosInstance.get(`/purchase-order/${newOrderId}/`);
          
          //daca inregistrarea exista
          if (checkResponse.status === 200) {
            recordExists = true;
            console.log(`Order ${newOrderId} found in database after ${attempts + 1} attempt(s)`);
            break;
          }
        } catch (error) {
          //daca primim statusul 404, inregistrarea nu exista inca
          if (error.response?.status === 404) {
            attempts++;
            console.log(`Attempt ${attempts}/${maxAttempts}: Order ${newOrderId} not found yet, retrying...`);
            
            if (attempts < maxAttempts) {
              //asteapta inainte de urmatoarea incercare
              await new Promise(resolve => setTimeout(resolve, delayMs));
            }
          } else {
            console.error("Error checking order existence:", error);
            throw error;
          }
        }
      }

      //daca dupa toate incercarile inregistrarea nu a fost gasita
      if (!recordExists) {
        throw new Error(`Order ${newOrderId} was not found in database after ${maxAttempts} attempts`);
      }

      //dupa ce se doveste ca inregistrarea exista sigur in DB, apeleaza comanda_modificata
      console.log(`Calling /purchase-order/${newOrderId}/comanda_modificata/`);
      await axiosInstance.put(
        `/purchase-order/${newOrderId}/comanda_modificata/`
      );

      // Navigare - isSubmitting rămâne true până la unmount
      // Folosim navigate cu replace pentru a evita revenirea
      navigate(-1);

    } catch (error) {
      console.error("Error:", error.response?.data || error);

      let errorMsg = "Failed to process request.";

      if (error.response?.data) {
        if (typeof error.response.data === "string") {
          errorMsg = error.response.data;
        } else if (error.response.data.detail) {
          errorMsg = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMsg = error.response.data.message;
        } else {
          errorMsg = JSON.stringify(error.response.data);
        }
      } else if (error.message) {
        errorMsg = error.message;
      }

      //afiseaza eroarea
      alert(errorMsg);
      
      // Doar în caz de eroare resetăm starea de submit
      setIsSubmitting(false);
    }
    // Nu mai avem finally aici - isSubmitting rămâne true la succes
  }, [
    validateForm,
    selectedProductsList,
    formData,
    id,
    navigate,
    roundToFourDecimals,
    isSubmitting,
    getCurrentExchangeRate,
    toNumber,
    unwrapId
  ]);

  const renderLoader = () => (
    <div className="loader-overlay">
      <div className="loader-container">
        <div className="spinner-large"></div>
        <p className="loader-text">Loading data...</p>
      </div>
    </div>
  );

  return (
    <div className='create-revision-page'>
      {isLoading && renderLoader()}

      <div className="create-revision-container">
        <div className="draft-header header-flex responsive-header po-header">
          <div className="left-section">
            <ArrowBackIcon className="back-arrow" onClick={() => navigate(-1)} />
            <div className="title-status-wrapper">
              <h2 className="create-revision-title">
                {formData.order_number}
              </h2>
              <span className={getStatusClass(formData.status)} style={{ marginLeft: '10px' }}>
                {formData.status}
              </span>
            </div>
          </div>

          <div className="right-section">
            <button
              className='btn save'
              onClick={handleSaveChanges}
              disabled={isSubmitting}
              style={{
                opacity: isSubmitting ? 0.7 : 1,
                cursor: isSubmitting ? 'not-allowed' : 'pointer'
              }}
            >
              {isSubmitting ? "Creating Revision..." : "Create Revision"}
            </button>
          </div>
        </div>
      </div>

      <hr className='title-separator' />

      <div className='create-revision-container'>
        <div className='card'>
          <h4>REVISION NOTES<span className='required-star'>*</span></h4>
          <textarea
            name='note_revision'
            value={formData.note_revision}
            onChange={handleChange}
            maxLength={1000}
            placeholder='Please provide a reason for creating this revision...'
            required
            className={errors.note_revision ? "input-error" : ""}
          />
          {errors.note_revision && (
            <p className='error-message' style={{color: 'red', fontSize: '12px', marginTop: '5px'}}>
              Please provide revision notes
            </p>
          )}
        </div>

        <div className='card card-select custom-card'>
          <div className='grid-3'>
            <div>
              <h4>SUPPLIER<span className='required-star'>*</span></h4>
              <Select 
                name="supplier"
                value={supplierOptions.find(option => String(option.value) === String(formData.supplier)) || null}
                onChange={(selectOption) => handleSelectChange(selectOption, { name: "supplier" })}
                options={supplierOptions}
                classNamePrefix='react-select'
                placeholder="--Select Supplier--"
                isSearchable={true}
                isClearable={true}
                required
                className={errors.supplier ? "input-error" : ""}
              />
              {errors.supplier && (
                <p className='error-message'>Supplier is required</p>
              )}
              {formData.supplier && selectedSupplierAddress && (
                <p className='supplier-address'>{selectedSupplierAddress}</p>
              )}
            </div>

            <div>
              <h4>DELIVERY ADDRESS<span className='required-star'>*</span></h4>
              <Select 
                name="delivery_address"
                value={deliveryAddressOptions.find(option => String(option.value) === String(formData.delivery_address)) || null}
                onChange={(selectOption) => handleSelectChange(selectOption, { name: 'delivery_address' })}
                options={deliveryAddressOptions}
                classNamePrefix='react-select'
                placeholder="--Select Delivery Address--"
                isSearchable={true} 
                isClearable={true}
                required 
                className={errors.delivery_address ? "input-error" : ""}
              />
              {errors.delivery_address && (
                <p className='error-message'>Delivery address is required</p>
              )}
              {formData.delivery_address && selectedDeliveryAddress && (
                <p className='supplier-address'>{selectedDeliveryAddress}</p>
              )}
              <label htmlFor='delivery_conditions' className="textarea-label"><strong>Delivery Conditions<span className='required-star'>*</span></strong></label>
              <textarea
                id="delivery_conditions"
                name="delivery_conditions"
                value={formData.delivery_conditions}
                onChange={handleChange}
                className={`textarea textarea_del_cond ${errors.delivery_conditions ? "input-error" : ""}`}
                placeholder="Specify delivery conditions here..."
                required
              />
              {errors.delivery_conditions && (
                <p className='error-message'>Delivery conditions are required</p>
              )}
            </div>

            <div>
              <h4>PROJECT CODE<span className='required-star'>*</span></h4>
              <Select 
                name="project_code"
                value={projectCodeOptions.find(option => String(option.value) === String(formData.project_code)) || null}
                onChange={(selectOption) => handleSelectChange(selectOption, { name: 'project_code' })}
                classNamePrefix='react-select'
                options={projectCodeOptions}
                placeholder="--Select Project Code--"
                isSearchable={true}
                isClearable={true}
                required
                className={errors.project_code ? "input-error" : ""}
              />
              {errors.project_code && (
                <p className='error-message'>Project code is required</p>
              )}

              <h4 style={{marginTop: '20px'}}>SUB-PROJECT CODE</h4>
              <Select
                name='subproject_code'
                value={subprojectCodeOptions.find(option => String(option.value) === String(formData.subproject_code)) || null}
                onChange={(selectOption) => handleSelectChange(selectOption, {name: 'subproject_code'})}
                options={subprojectCodeOptions}
                classNamePrefix="react-select"
                placeholder={isSubprojectCodeDisabled ? "--Select Project Code first--" : "--Select Sub-Project Code--"}
                isSearchable={true}
                isClearable={true} 
                isDisabled={isSubprojectCodeDisabled}
              />
            </div>
          </div>
        </div>

        <div className='card'>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div>
              <h4>SHIPMENT DETAILS</h4>
              <label>Arrival Deadline<span className='required-star'>*</span></label>
              <div className="datepicker-container">

                <DatePicker
                  selected={arrivalDeadline}
                  onChange={handleDateChange}
                  dateFormat="dd.MM.yyyy"
                  placeholderText="dd.mm.yyyy"
                  className={`datepicker-input ${errors.arrival_deadline ? "input-error" : ""}`}
                  required
                  wrapperClassName="datepicker-wrapper"
                />
                <span className="calendar-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </span>
              </div>
              {errors.arrival_deadline && (
                <p className='error-message'>Arrival deadline is required</p>
              )}
            </div>

            <div>
              <h4>PAYMENT</h4>
              <label>Payment Term<span className='required-star'>*</span></label>
              <div className="payment-wrapper">
                <input
                    type="number"
                    name="payment_term"
                    value={formData.payment_term}
                    onChange={handleChange}
                    className={`payment-input ${errors.payment_term ? "input-error" : ""}`}
                    placeholder="Enter number of days..."
                    min="1"
                    step="1"
                />
                {formData.payment_term && (
                  <span className="days-text">
                    days
                  </span>
                )}
              </div>
              {errors.payment_term && (
                <p className='error-message'>Payment term is required</p>
              )}
            </div>

            <div>
              <h4>CURRENCY</h4>
              <label>Select Currency<span className='required-star'>*</span></label>
              <select
                name='currency'
                value={formData.currency}
                onChange={handleChange}
                className={errors.currency ? "input-error" : ""}
              >
                <option value="">--Select Currency--</option>
                {currency.map((currencies) => (
                  <option key={currencies.id} value={currencies.id}>
                    {currencies.name}
                  </option>
                ))}
              </select>
              {errors.currency && (
                <p className='error-message'>Currency is required</p>
              )}
            </div>

            <div>
              <h4>EXCHANGE RATE</h4>
              <label>Select Exchange Rate{(!(systemCurrencyId === parseInt(formData.currency)) && formData.currency) && <span className='required-star'>*</span>}</label>
              {formData.currency ? (
                (() => {
                  const currencyId = Number(formData.currency);
                  const exchangeOpts = exchangeOptionsByCurrencyId.get(currencyId) || [];
                  const isSystemCurrency = systemCurrencyId === currencyId;
                  
                  if (isSystemCurrency) {
                    return (
                      <input
                        type="text"
                        value="Not needed (system currency)"
                        disabled
                        className="input-disabled"
                      />
                    );
                  }
                  
                  if (exchangeOpts.length === 0) {
                    return (
                      <input
                        type="text"
                        value="No exchange rates available"
                        disabled
                        className="input-disabled"
                      />
                    );
                  }
                  
                  return (
                    <>
                      <select
                        name="exchange_rate"
                        value={formData.exchange_rate || ""}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            exchange_rate: e.target.value
                          }));
                          if (errors.exchange_rate) {
                            setErrors(prev => ({ ...prev, exchange_rate: "" }));
                          }
                        }}
                        className={errors.exchange_rate ? "input-error" : ""}
                        required={!isSystemCurrency}
                      >
                        <option value="">--Select Exchange Rate--</option>
                        {exchangeOpts.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {errors.exchange_rate && (
                        <p className="error-message">Exchange rate is required</p>
                      )}
                    </>
                  );
                })()
              ) : (
                <select disabled className="input-disabled">
                  <option>--Select currency first--</option>
                </select>
              )}
            </div>
          </div>
        </div>

        <div className='card'>
          <h4>ADD PRODUCTS</h4>
          <div className='products'>
            <input
              type='text'
              placeholder='Search products'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="off"
              className='search-input'
            />
            <SearchIcon className='search-icon' />
            <button onClick={() => setShowModal(true)}>Browse</button>

            {searchTerm && (
              <ul className='dropdown-list'>
                {filteredProducts.length > 0 ? (
                  filteredProducts.map(prod => (
                    <li
                      key={prod.id}
                      onClick={() => handleProductSelect(prod)}
                      className="dropdown-item"
                      onMouseDown={e => e.preventDefault}
                    >
                      {prod.title}
                    </li>
                  ))
                ) : (
                  <li className='dropdown-item no-result'>No available products</li>
                )}
              </ul>
            )}
          </div>
          {errors.product && (
            <p className='error-message' style={{marginTop: '10px'}}>At least one product is required</p>
          )}
          {selectedProductsList.length > 0 && (
            <div className='table-responsive'>
              <table className='product-table'>
                <thead>
                  <tr>
                    <th>Products</th>
                    <th>Quantity<span className='required-star'>*</span></th>
                    <th>UM<span className='required-star'>*</span></th>
                    <th>VAT (%)<span className='required-star'>*</span></th>
                    <th>Price per unit<span className='required-star'>*</span></th>
                    <th>Total</th>
                    <th>System Total ({getSystemCurrencyLabel()})</th>
                    <th>Arrival Deadline<span className='required-star'>*</span></th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {selectedProductsList.map((prod, index) => (
                    <tr key={index}>
                      <td>{prod.title}</td>
                      <td>
                        <NumericFormat
                          value={prod.quantity}
                          thousandSeparator="."
                          decimalSeparator=","
                          decimalScale={4}
                          fixedDecimalScale={false}
                          allowNegative={false}
                          inputMode="decimal"
                          customInput="input"
                          className={errors[`quantity_${index}`] ? "input-error" : ""}
                          onValueChange={(values) =>
                            handleProductLineChange(index, "quantity", values.floatValue ?? 0)
                          }
                        />
                        {errors[`quantity_${index}`] && (
                          <span className="field-error">Required</span>
                        )}
                      </td>
                      <td>
                        <input
                          type="text"
                          value={prod.um}
                          className={errors[`um_${index}`] ? "input-error" : ""}
                          onChange={e => handleProductLineChange(index, "um", e.target.value)}
                        />
                        {errors[`um_${index}`] && (
                          <span className="field-error">Required</span>
                        )}
                      </td>
                      <td>
                        <NumericFormat
                          value={prod.vat}
                          thousandSeparator="."
                          decimalSeparator=","
                          decimalScale={4}
                          fixedDecimalScale={false}
                          allowNegative={false}
                          inputMode="decimal"
                          customInput="input"
                          className={errors[`vat_${index}`] ? "input-error" : ""}
                          onValueChange={(values) =>
                            handleProductLineChange(index, "vat", values.floatValue ?? 0)
                          }
                        />
                        {errors[`vat_${index}`] && (
                          <span className="field-error">Required</span>
                        )}
                      </td>
                      <td>
                        <NumericFormat
                          value={prod.cost}
                          thousandSeparator="."
                          decimalSeparator=","
                          decimalScale={4}
                          fixedDecimalScale={false}
                          allowNegative={false}
                          inputMode="decimal"
                          customInput="input"
                          className={errors[`cost_${index}`] ? "input-error" : ""}
                          onValueChange={(values) =>
                            handleProductLineChange(index, "cost", values.floatValue ?? 0)
                          }
                        />
                        {errors[`cost_${index}`] && (
                          <span className="field-error">Required</span>
                        )}
                      </td>
                      <td>
                        <NumericFormat
                          value={prod.total || 0}
                          thousandSeparator="."
                          decimalSeparator=","
                          decimalScale={2}
                          fixedDecimalScale={true}
                          allowNegative={false}
                          inputMode="decimal"
                          customInput="input"
                          readOnly
                        />
                      </td>
                      <td>
                        <NumericFormat
                          value={prod.total_system_currency || 0}
                          thousandSeparator="."
                          decimalSeparator=","
                          decimalScale={2}
                          fixedDecimalScale={true}
                          allowNegative={false}
                          inputMode="decimal"
                          customInput="input"
                          readOnly
                        />
                      </td>
                      <td>
                        <div className="datepicker-field">
                          <DatePicker
                            selected={prod.arrival_deadline ? new Date(prod.arrival_deadline) : null}
                            onChange={(date) => {
                                if (!date) {
                                    handleProductLineChange(index, "arrival_deadline", "");
                                    return;
                                }
                                const year = date.getFullYear();
                                const month = String(date.getMonth() + 1).padStart(2, "0");
                                const day = String(date.getDate()).padStart(2, "0");
                                const formattedDate = `${year}-${month}-${day}`;
                                handleProductLineChange(index, "arrival_deadline", formattedDate);
                            }}
                            dateFormat="dd.MM.yyyy"
                            placeholderText="dd.mm.yyyy"
                            className={`arrival_date ${errors[`arrival_${index}`] ? "input-error" : ""}`}
                            wrapperClassName="datepicker-wrapper"
                            popperProps={{ strategy: "fixed" }}
                            popperContainer={({ children }) => (
                              <div style={{ position: "fixed", zIndex: 99999 }}>
                                {children}
                              </div>
                            )}
                          />
                          <span className="calendar-icon">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                              <line x1="16" y1="2" x2="16" y2="6" />
                              <line x1="8" y1="2" x2="8" y2="6" />
                              <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                          </span>
                        </div>
                        {errors[`arrival_${index}`] && (
                          <span className="field-error">Required</span>
                        )}
                      </td>
                      <td>
                        <button onClick={() => handleAskDeleteProduct(index)} aria-label="delete" size="small">
                          <DeleteIcon style={{ color: 'red' }} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td style={{ verticalAlign: 'middle' }}>
                      <strong style={{ 
                        fontSize: '16px', 
                        fontWeight: 600, 
                        borderLeft: '3px solid #2e5bee', 
                        paddingLeft: '12px',
                        display: 'inline-block',
                        lineHeight: '42px'
                      }}>
                        Taxes
                      </strong>
                    </td>
                    <td colSpan={2}>
                      <select
                        name="tax"
                        value={formData.tax}
                        onChange={handleChange}
                        style={{ width: '100%', margin: 0 }}
                      >
                        <option value="">--Select taxes--</option>
                        {taxes.map((tax) => (
                          <option key={tax.id} value={tax.id}>
                            {tax.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <NumericFormat
                        value={formData.vat_value_tax}
                        onValueChange={({ value }) =>
                          handleChange({ target: { name: "vat_value_tax", value: value || "0" } })
                        }
                        thousandSeparator={false}
                        decimalSeparator=","
                        decimalScale={4}
                        allowNegative={false}
                        fixedDecimalScale={false}
                        inputMode="decimal"
                        readOnly={!formData.tax}
                        style={{ margin: 0 }}
                        className={errors.vat_value_tax ? "input-error" : ""}
                      />
                      {errors.vat_value_tax && (
                        <span className="field-error">Required</span>
                      )}
                    </td>
                    <td>
                      <NumericFormat
                        value={formData.tax_value}
                        onValueChange={({ value }) =>
                          handleChange({ target: { name: "tax_value", value: value || "0" } })
                        }
                        thousandSeparator={false}
                        decimalSeparator=","
                        decimalScale={4}
                        allowNegative={false}
                        fixedDecimalScale={false}
                        inputMode="decimal"
                        readOnly={!formData.tax}
                        style={{ margin: 0 }}
                        className={errors.tax_value ? "input-error" : ""}
                      />
                      {errors.tax_value && (
                        <span className="field-error">Required</span>
                      )}
                    </td>
                    <td>
                      <NumericFormat
                        value={Number(orderSummary?.total_delivery || 0)}
                        thousandSeparator="."
                        decimalSeparator=","
                        decimalScale={2}
                        fixedDecimalScale={true}
                        displayType="input"
                        readOnly
                        className="input"
                        style={{ margin: 0 }}
                      />
                    </td>
                    <td>
                      <NumericFormat
                        value={Number(orderSummary?.total_delivery_system_currency || 0)}
                        thousandSeparator="."
                        decimalSeparator=','
                        decimalScale={2}
                        fixedDecimalScale={true}
                        displayType='input'
                        className='input'
                        readOnly
                        style={{margin: 0}}
                      />
                    </td>
                    <td></td>
                  </tr>
                  <tr>
                    <td style={{ fontSize: '12px' }} colSpan={9}>
                      {selectedProductsList.length} variants on purchase order
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className='grid-2'>
          <div className='card'>
            <h4>ADDITIONAL DETAILS</h4>
            <h4>Note to Supplier</h4>
            <textarea
              name='note_to_supplier'
              value={formData.note_to_supplier}
              onChange={handleChange}
              maxLength={5000}
              placeholder='Write a note...'
            />
            <div className='char-count'>{(formData.note_to_supplier || '').length}/5000</div>
          </div>

          <div className='card'>
            <h4>COST SUMMARY</h4>
            <hr/>
            {isCalculating ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                Calculating...
              </div>
            ) : (
              (() => {
                const summary = orderSummary || {
                  total_without_vat: 0,
                  total_vat: 0,
                  delivery_tax: 0,
                  delivery_vat: 0,
                  total_delivery: 0,
                  total: 0,
                  total_without_vat_system: 0,
                  total_vat_system: 0,
                  delivery_tax_system: 0,
                  total_delivery_system_currency: 0,
                  total_system_currency: 0
                };
                const currencyLabel = getSelectedCurrencyLabel();
                const systemCurrencyLabel = getSystemCurrencyLabel();

                const formatValue = (value) => {
                  return Number(value).toLocaleString('ro-RO', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  });
                };

                return (
                  <div className="summary-container">
                    <table className="summary-table">
                      <thead>
                        <tr className="table-header">
                          <th className="cell-left"></th>
                          <th className="cell-right header-currency">Total Currency</th>
                          <th className="cell-right header-system">System Total ({systemCurrencyLabel})</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="table-row">
                          <td className="cell-left label">Total without VAT</td>
                          <td className="cell-right with-arrow">
                            {formatValue(summary.total_without_vat)} {currencyLabel}
                          </td>
                          <td className="cell-right system-value">
                            {formatValue(summary.total_without_vat_system)} {systemCurrencyLabel}
                          </td>
                        </tr>
                        <tr className="table-row">
                          <td className="cell-left label">Taxes without VAT</td>
                          <td className="cell-right with-arrow">
                            {formatValue(summary.delivery_tax)} {currencyLabel}
                          </td>
                          <td className="cell-right system-value">
                            {formatValue(summary.delivery_tax_system)} {systemCurrencyLabel}
                          </td>
                        </tr>
                        <tr className="table-row">
                          <td className="cell-left label">VAT</td>
                          <td className="cell-right with-arrow">
                            {formatValue(summary.total_vat)} {currencyLabel}
                          </td>
                          <td className="cell-right system-value">
                            {formatValue(summary.total_vat_system)} {systemCurrencyLabel}
                          </td>
                        </tr>
                        <tr className="total-row">
                          <td className="cell-left total-label">TOTAL</td>
                          <td className="cell-right total-value with-arrow">
                            <strong>{formatValue(summary.total)} {currencyLabel}</strong>
                          </td>
                          <td className="cell-right total-system-value">
                            <strong>{formatValue(summary.total_system_currency)} {systemCurrencyLabel}</strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                );
              })()
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <BrowseProduct
          onClose={() => setShowModal(false)}
          productList={product}
          onSelectProducts={(selectedProducts) => {
            const newProducts = selectedProducts.map(prod => ({
              ...prod,
              quantity: "",
              um: "",
              vat: "21",
              cost: "",
              total: 0,
              total_system_currency: 0,
              subtotal: 0,
              vat_amount: 0,
              arrival_deadline: formData.arrival_deadline || ""
            }));

            setSelectedProductsList(prev => {
              const existingIds = new Set(prev.map(p => p.id));
              const filteredNew = newProducts.filter(p => !existingIds.has(p.id));
              return [...prev, ...filteredNew];
            });

            setShowModal(false);
          }}
        />
      )}

      {showConfirmModal && (
        <div className="confirm-overlay">
          <div className="confirm-box">
            <p>Are you sure you want to delete this product?</p>
            <div className="confirm-actions">
              <button onClick={confirmDeleteProduct} className="btn btn-danger">Yes, delete</button>
              <button onClick={() => setShowConfirmModal(false)} className="btn btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatePORevision;